import { Shimmer } from "@/components/ai-elements/shimmer";
import { CircleAlertIcon, FileCodeIcon } from "lucide-react";
import { AgentAvatar } from "./AgentAvatar.js";
import { cn } from "./cn.js";
import { AGENTS, type AgentRoom } from "./nav.js";
import type { WebsiteLive } from "./site-generative.js";

type Props = {
	live: WebsiteLive | null;
	title: string;
};

/** The three roster personas who share the redesign workflow. */
type CrewId = "creative" | "content" | "developer";
const CREW: CrewId[] = ["creative", "content", "developer"];

type PhaseBeat = {
	agent: CrewId;
	title: (brand: string) => string;
	line: string;
};

/**
 * Which persona owns each workflow phase, per the roster: Kai (creative) owns
 * inventory/IA/layout/media, Iris (content) owns copy, Rhea (developer) owns
 * the Astro build. These are step names on one workflow, not separate agents.
 */
const PHASE_BEAT: Record<string, PhaseBeat> = {
	started: {
		agent: "developer",
		title: (brand) => `Rhea is setting up ${brand}'s new site`,
		line: "Opening the project…",
	},
	cloning: {
		agent: "developer",
		title: (brand) => `Rhea is setting up ${brand}'s new site`,
		line: "Opening the project…",
	},
	inventorying: {
		agent: "creative",
		title: (brand) => `Kai is studying ${brand}'s current site`,
		line: "Reading every page for brand, offers, and structure…",
	},
	briefing: {
		agent: "creative",
		title: () => "Kai is shaping the plan",
		line: "Deciding what to keep and what to add…",
	},
	designing: {
		agent: "creative",
		title: () => "Kai is designing the new site",
		line: "Choosing the layout, type, and palette…",
	},
	copywriting: {
		agent: "content",
		title: () => "Iris is writing the copy",
		line: "Headlines and pages in the brand's own voice…",
	},
	media: {
		agent: "creative",
		title: () => "Kai is creating the photography",
		line: "Making images that match the brand…",
	},
	framing: {
		agent: "developer",
		title: () => "Rhea is setting up the Astro project",
		line: "Laying the project foundations…",
	},
	awaiting_design: {
		agent: "developer",
		title: (brand) => `Rhea is building ${brand}'s new site`,
		line: "Writing the next page…",
	},
	generating: {
		agent: "developer",
		title: (brand) => `Rhea is building ${brand}'s new site`,
		line: "Writing the pages…",
	},
	progress: {
		agent: "developer",
		title: (brand) => `Rhea is building ${brand}'s new site`,
		line: "Building the site…",
	},
	building: {
		agent: "developer",
		title: (brand) => `Rhea is building ${brand}'s new site`,
		line: "Putting the preview together…",
	},
};

const FALLBACK_BEAT: PhaseBeat = {
	agent: "developer",
	title: (brand) => `Rhea is building ${brand}'s new site`,
	line: "Building the site…",
};

/**
 * The preview canvas while the site is being built: the crew (Kai, Iris,
 * Rhea) front and center with the active persona highlighted and a live
 * narration line — not a fake page skeleton. The finished site replaces this
 * pane in place the moment the preview is ready.
 */
export function SiteGenerativePane({ live, title }: Props) {
	const brand = live?.name?.trim() || title;
	const failed = live?.status === "failed" || live?.phase === "failed";
	const lastFile = live?.lastFile ?? null;
	const writing = Boolean(lastFile) && !failed;
	const beat =
		(live ? (PHASE_BEAT[live.phase] ?? PHASE_BEAT[live.status]) : undefined) ??
		FALLBACK_BEAT;
	const progress = Math.min(Math.max(live?.progress ?? 0, 0), 100);
	const files = live?.filesWritten ?? [];
	const crew = CREW.map((id) => AGENTS.find((agent) => agent.id === id)).filter(
		(agent): agent is AgentRoom => Boolean(agent),
	);

	return (
		<div
			className={cn("mc-site-build", failed && "mc-site-build--failed")}
			aria-live="polite"
			aria-busy={!failed}
		>
			<div className="mc-site-build-core">
				<div className="mc-site-build-crew" aria-hidden>
					{crew.map((agent) => {
						const active = !failed && agent.id === beat.agent;
						return (
							<figure
								key={agent.id}
								className={cn(
									"mc-site-build-member",
									active && "mc-site-build-member--active",
								)}
							>
								<span className="mc-site-build-member-avatar">
									<AgentAvatar agent={agent} size={active ? "lg" : "md"} />
									{active ? <span className="mc-site-build-ring" /> : null}
								</span>
								<figcaption>{agent.name}</figcaption>
							</figure>
						);
					})}
				</div>
				<h2 className="mc-site-build-title">
					{failed ? "The build hit a snag" : beat.title(brand)}
				</h2>
				{failed ? (
					<p className="mc-site-build-error">
						<CircleAlertIcon className="size-3.5 shrink-0" />
						{live?.lastError ?? "Ask Mairko to start the build again."}
					</p>
				) : (
					<Shimmer className="mc-site-build-status" as="p">
						{writing && lastFile ? `Writing ${shortPath(lastFile)}` : beat.line}
					</Shimmer>
				)}
				{!failed && progress > 0 ? (
					<div
						className="mc-site-build-progress"
						role="progressbar"
						aria-valuenow={progress}
						aria-valuemin={0}
						aria-valuemax={100}
					>
						<span style={{ width: `${progress}%` }} />
					</div>
				) : null}
				{!failed ? (
					<p className="mc-site-build-note">
						The finished site will appear right here — no need to refresh.
					</p>
				) : null}
			</div>
			{!failed && files.length > 0 ? (
				<div className="mc-site-build-files" aria-label="Files written so far">
					{files.slice(-6).map((file) => (
						<span
							key={file}
							className={cn(
								"mc-site-build-file",
								file === lastFile && "mc-site-build-file--live",
							)}
						>
							<FileCodeIcon className="size-3" />
							{shortPath(file)}
						</span>
					))}
					{files.length > 6 ? (
						<span className="mc-site-build-file mc-site-build-file--count">
							{files.length} files
						</span>
					) : null}
				</div>
			) : null}
		</div>
	);
}

function shortPath(path: string): string {
	const parts = path.split("/").filter(Boolean);
	if (parts.length <= 2) return path;
	return parts.slice(-2).join("/");
}
